import { Howl, Howler } from 'howler';
import { TagType } from '@/_models/Tag';
import { PolygonActionType } from '@/_models/virtualStaging/Polygon';
import { TourActionType } from '@/_models/virtualStaging/VSObject';
import * as all from '@/components/util/all';
import isValidUrl from '@/utils/isValidUrl';
import { ExportAudio, MusicObj, MusicObjectKey, TMusicData, TMusicDataEntry, TPlaylist } from './types';
import { emitter } from '@/utils/emiter';
import { MittEvents } from '@/components/types';

export { MusicObj, MusicObjectKey };

Howler.autoSuspend = false;

export class AudioModule {
  // @ts-ignore
  private musicData: TMusicData = {};

  private videosForSoundControl = [];

  private volumeValue = 1;

  private isAllMusicMute = false;

  private lowestPriotiryLevel = 10;

  private nowPlayingPriorityLevel;

  private playList: TPlaylist[] = [];

  private isGreenScreenPlaying = false;

  private repeatCount = 3;

  private isTourAudioPaused = true;

  private eventDebounceId;

  private static instance: AudioModule;

  public static getInstance(): AudioModule {
    if (!AudioModule.instance) {
      AudioModule.instance = new AudioModule();
    }

    return AudioModule.instance;
  }

  private removeAudioFromPlayList = (type: MusicObjectKey, id: string | number): void => {
    this.playList = this.playList.filter((el) => el.id !== `${id}`);
  };

  private async nextMusic(skipTourMusic = false): Promise<void> {
    if (this.isGreenScreenPlaying || !this.playList.length) return;

    try {
      const firstMusicIsTour = this.playList[0]?.type.name === MusicObjectKey.TOUR;
      const music = skipTourMusic && firstMusicIsTour ? this.playList[1] : this.playList[0];

      if (!music) return;

      await this.repeatNextMusicCall(music.audio);

      if (music.type.name === MusicObjectKey.TOUR) this.isTourAudioPaused = false;

      this.nowPlayingPriorityLevel = music.priority;
    } catch (err) {
      console.error(`Can't play next music. Message: ${err}`);
      this.playList.shift();
      await this.nextMusic();
    }
  }

  private removeDuplicatedAudio = (id: string, type?: MusicObjectKey): void => {
    this.playList.filter((audio) => type === audio.type.name || id === audio.id).forEach(({ audio }) => audio.pause());
    this.playList = this.playList.filter((audio) => audio.id !== id && (!type || type !== audio.type.name));
  };

  private sleep = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  private repeatMusicCall = async (type: MusicObjectKey, objectId: string): Promise<void> => {
    await this.startPlay(this.getAudio(type, objectId), 0);
  };

  private repeatNextMusicCall = async (nextAudio): Promise<void> => {
    await this.startPlay(nextAudio, 0);
  };

  private setVSAudiosVolume = (value: number) => {
    this.videosForSoundControl?.forEach((videoNode) => {
      const { video } = videoNode;
      if (value === 0) {
        video.muted = true;
      } else {
        video.volume = value;
        // Unmuting only videos that were unmuted before
        if (!videoNode?.muteBtn?.active) {
          video.muted = false;
        }
      }
    });
  };

  private startPlay = async (audio: Howl, index) => {
    if (index >= this.repeatCount) return;

    try {
      // Pause currently playing audio before playing new one
      this.pauseAllMusic();
      // Mute VS Audios if any are playing
      this.setVSAudiosVolume(0);
      audio?.mute(this.isAllMusicMute || this.volumeValue === 0);
      audio?.play();
    } catch (e) {
      await this.sleep(500);
      await this.startPlay(audio, index + 1);
    }
  };

  private setTourMusic = (value: boolean) => {
    this.isTourAudioPaused = value;
  };

  private isPlaying = (id): boolean => {
    return this.playList.some((i) => i.id === `${id}` && i.audio.playing());
  };

  getVolume = () => {
    return this.volumeValue;
  };

  getAllMusicData = () => {
    return this.musicData;
  };

  isTourMusicPaused = () => {
    return this.isTourAudioPaused;
  };

  getAudioByType = (type: MusicObjectKey): TMusicDataEntry => {
    return this.musicData?.[type];
  };

  getAudio = (type: MusicObjectKey, id: number | string): Howl | undefined => {
    return this.musicData?.[type]?.[`${id}`];
  };

  getTourMusic = () => {
    return this.playList.find((el) => el.type.name === MusicObjectKey.TOUR);
  };

  setGreenScreenPlaying = (value: boolean) => {
    this.isGreenScreenPlaying = value;
  };

  stopTourMusic = async () => {
    // Passing false to prevent removing tour audio from playlist
    // This will ensure that the tour/floor music resumes after the current audio ends
    // This is a fix for TB-3423
    // In case there's a bug in the future where the tour/floor music plays when it shouldn't, this is the first place to check
    await this.stopAudioByType(MusicObjectKey.TOUR, false);
    await this.stopAudioByType(MusicObjectKey.FLOORS, false);
    this.setTourMusic(true);
  };

  changeSelectedAudioLoop = (type: MusicObjectKey, id: number | string, value: boolean) => {
    this.getAudio(type, id)?.loop(value);
  };

  changeSelectedAudioVolume = (type: MusicObjectKey, id: number | string, value: number) => {
    this.getAudio(type, id)?.volume(value);
  };

  getFirstMusic = (): TPlaylist => {
    return this.playList[0];
  };

  resumeMusic = async (): Promise<void> => {
    await this.nextMusic();
  };

  pauseAudio = async (id: number | string): Promise<void> => {
    const audio = this.playList.find((a) => a.id === `${id}`)?.audio;
    audio?.pause();
    // If there are other audios in the playlist, place current audio at the end of the playlist and play next audio
    if (this.playList.length > 1) {
      this.playList.push(this.playList.shift());
      await this.nextMusic();
    }
  };

  resumeAudio = async (id: number | string): Promise<void> => {
    // Pausing currently playing audio before resuming
    this.getFirstMusic().audio?.pause();
    this.playList.find((audio) => audio.id === `${id}`)?.audio?.play();
  };

  stopAudioByType = async (type: MusicObjectKey, removeFromPlaylist = true): Promise<void> => {
    // Unmute any VS Videos playing
    this.setVSAudiosVolume(this.volumeValue);

    this.playList.filter((el) => el.type.name === type).forEach(({ audio }) => audio.pause());
    if (removeFromPlaylist) {
      this.playList = this.playList.filter((el) => el.type.name !== type);
    }

    // To avoid tour music playing again after pausing, we pass 'true' (TB-3478)
    await this.nextMusic(true);
  };

  pauseAllMusic = (overridePriority = true): void => {
    this.playList.forEach((i) => {
      i.audio.pause();

      // to change play btn icon
      if (i.type.name === MusicObjectKey.TOUR || i.type.name === MusicObjectKey.FLOORS) {
        emitter.emit(MittEvents.TOUR_AUDIO_PLAY, false);
      }
    });
    if (overridePriority) {
      this.nowPlayingPriorityLevel = this.lowestPriotiryLevel;
    }
  };

  addMusicToVolumeControl = (el) => {
    this.videosForSoundControl.push(el);
  };

  allPlayingVideosAreMuted = () => {
    return !this.videosForSoundControl.some((el) => !el.video.paused && el.video.volume > 0 && !el.video.muted);
  };

  muteHandler = (state: boolean): void => {
    this.isAllMusicMute = state;
    Object.values(this.musicData).forEach((data) => {
      Object.values(data).forEach((el) => {
        el.mute(state);
      });
    });
  };

  volumeHandler = (value: number): void => {
    if (!value && value !== 0) return;

    this.volumeValue = value;

    this.setVSAudiosVolume(value);

    Object.values(this.musicData).forEach((data) => {
      Object.values(data).forEach((el) => {
        if (value === 0) {
          el.mute(true);
        } else {
          el.volume(value);
          el.mute(false);
        }
      });
    });

    // Clear existing timeout to cancel previous delayed operation
    clearTimeout(this.eventDebounceId);

    // Delay execution of the emitter.emit until 500ms after this function is last executed
    this.eventDebounceId = setTimeout(() => {
      emitter.emit(MittEvents.AUDIO_VOLUME, this.volumeValue);
    }, 500);
  };

  tourHasAudio = (): boolean => {
    return Object.keys(this.musicData).length > 0 || this.videosForSoundControl.length > 0;
  };

  // Removing all audios from playlist except tour audio
  // By the new request from Ely, now we don't resume songs if they got interrupted (exept tour audio)
  clearPlayListQueue = (): void => {
    this.playList.forEach((el) => {
      if (el.type.name !== MusicObjectKey.TOUR && el.type.name !== MusicObjectKey.FLOORS) {
        // reseting audio for when it's played again
        const audio = this.getAudio(el.type.name, el.id);
        if (!audio) return;
        audio.stop();
        // removing from playlist
        this.removeAudioFromPlayList(el?.type?.name, el?.id);
      }
    });
  };

  playAudio = async (data: ExportAudio): Promise<void> => {
    const {
      type: { name: type, priority },
      id,
    } = data;

    if (!this.getAudio(type, id) || this.isPlaying(id)) {
      return;
    }
    const playListAudio = {
      id: `${id}`,
      audio: this.getAudio(type, id),
      priority,
      type: data.type,
    };
    if (this.nowPlayingPriorityLevel >= priority || !this.nowPlayingPriorityLevel) {
      try {
        const isTourMusic = type === MusicObjectKey.TOUR || type === MusicObjectKey.FLOORS;
        emitter.emit(MittEvents.TOUR_AUDIO_PLAY, isTourMusic);

        if (isTourMusic) {
          this.isTourAudioPaused = false;
        }
        this.nowPlayingPriorityLevel = priority;
        if (type === MusicObjectKey.SWEEPGROUPS) {
          // remove all existing sweep audio to prevent play it after new sweep audio ends
          this.removeDuplicatedAudio(playListAudio.id, MusicObjectKey.SWEEPGROUPS);
        } else {
          this.removeDuplicatedAudio(playListAudio.id);
        }

        this.startPlay(this.getAudio(type, id), 0);

        this.clearPlayListQueue();
        this.playList.unshift(playListAudio);
      } catch (err) {
        await this.repeatMusicCall(type, `${id}`);
        if (!this.getAudio(type, id).playing()) {
          console.error(`Can't play ${type} audio. Message: ${err}`);
        } else {
          this.pauseAllMusic();
        }
      }
      return;
    }
    const index = this.playList.findIndex((el) => el.priority > priority);
    this.removeDuplicatedAudio(playListAudio.id);
    if (index) {
      this.playList.splice(index - 1, 0, playListAudio);
    } else {
      this.playList.push(playListAudio);
    }
  };

  // Import music from requests to musicData
  private urlHandling = (type: MusicObjectKey, data: Record<string, unknown>): string => {
    let src: string;
    const awsUrl = process.env.REACT_APP_AWS;
    switch (type) {
      case MusicObjectKey.POLYGONS:
        if (data.url && data.actionType === PolygonActionType.Music) {
          src = `${awsUrl}/tours/${data.tourId}/polygons/${data.url}`;
        }
        break;
      case MusicObjectKey.PANORAMAS:
        if (data.url) {
          src = `${awsUrl}/tours/${data.tourId}/panoramas/${data.panoramaUrl}/${data.url}`;
        }
        break;
      case MusicObjectKey.TAGS:
        if (data.url && data.type === TagType.Music) {
          src = `${awsUrl}/tours/${data.tourId}/tags/${data.url}`;
        }
        break;
      case MusicObjectKey.TOUR:
        src = `${awsUrl}/tours/${data.id}/music.mp3${all.getVersion(data.updatedAt)}`;
        break;
      case MusicObjectKey.FLOORS:
        if (data.url) {
          src = `${data.url}`;
        }
        break;
      case MusicObjectKey.SWEEPGROUPS:
        if (data.music) {
          src = `${awsUrl}/tours/${data.tourId}/sweeps/${data.music}`;
        }
        break;
      case MusicObjectKey.VSIMAGES:
        if (data.actionURL && data.actionType === TourActionType.Music) {
          if (data.actionURL.toString().includes('http')) {
            src = data.actionURL.toString();
          } else {
            src = `${awsUrl}/tours/${data.tourId}/vsimages/${data.actionURL}`;
          }
        }
        break;
      case MusicObjectKey.BUTTON:
        if (data.buttonSoundUrl) {
          src = `${awsUrl}/tours/${data.tourId}/game/${data.id}/${data.buttonSoundUrl}`;
        }
        break;
      case MusicObjectKey.GAME_SELECTED_AUDIO:
        if (data.sound) {
          src = `${process.env.REACT_APP_AWS}/games/${data.gameId}/locks/${data.id}/${data.sound}`;
        }
        break;
      default:
        break;
    }
    return src;
  };

  private createAudioStream = async (type: MusicObjectKey, data: Record<string, unknown>) => {
    // If a tour audio already exists - don't add a new one
    if (type === MusicObjectKey.TOUR && this.getAudioByType(type)) return;

    const src = this.urlHandling(type, data);
    const objectId = data.id.toString();
    const isValid = await isValidUrl(src);

    if (isValid) {
      const audioObject = new Howl({
        src,
        autoplay: false,
        mute: this.isAllMusicMute,
        volume: this.volumeValue,
      });

      audioObject.once('end', async () => {
        audioObject.unload();
        this.removeAudioFromPlayList(type, objectId);
        await this.nextMusic();
      });

      // @ts-ignore
      audioObject.id = objectId;

      this.musicData[type] = { ...this.getAudioByType(type), [objectId]: audioObject };
    }
  };

  importAudioData = async (type: MusicObjectKey, data: Record<string, unknown>[]) => {
    const promises = data?.map((element) => this.createAudioStream(type, element)).filter(Boolean);
    if (promises) return Promise.all(promises);
    return Promise.resolve();
  };
}
